import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';

import { ConfigService } from '../../shared/services/config.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { XeroService } from '../../shared/services/xero.service';
import { MatDialog } from '@angular/material/dialog';
import { AccountTaxDialogComponent } from './account-tax-dialog/account-tax-dialog.component';
import { ToastrService } from 'ngx-toastr';
import { MatButton } from '@angular/material/button';
import { NgSwitch, NgSwitchCase } from '@angular/common';

@Component({
    selector: 'xero-setup',
    templateUrl: './xero-setup.component.html',
    providers: [XeroService],
    standalone: true,
    imports: [NgSwitch, NgSwitchCase, MatButton]
})
export class XeroSetupComponent implements OnInit, OnDestroy {
  status: 'unknown' | 'connecting' | 'connected' | 'disconnected' | 'error' =
    'unknown';

  private ngUnsubscribe = new Subject<void>();
  queryParams: Params;
  importingContacts = false;

  constructor(
    private router: Router,
    private configService: ConfigService,
    private route: ActivatedRoute,
    private xeroService: XeroService,
    private dialog: MatDialog,
    private toastService: ToastrService
  ) {
    this.queryParams = this.route.snapshot.queryParams;
  }

  ngOnInit(): void {
    if (this.queryParams.integration) {
      if (this.queryParams.integration === 'xero') {
        this.status = 'connecting';
        this.connectAccount();
      }
    } else {
      this.checkStatus();
    }
  }

  getConnectionUrl() {
    this.xeroService
      .getXeroConnectionUrl()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        (res: any) => {
          location.href = res.url;
        },
        (error) => {
          this.handleError(error);
        }
      );
  }

  connectAccount() {
    const { code } = this.queryParams;
    this.xeroService
      .registerXeroConfiguration(code)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        () => {
          this.router.navigate(['/settings']);
          this.status = 'connected';
          this.openAccountTaxDialog();
        },
        (error) => {
          this.handleError(error);
        }
      );
  }

  disconnect() {
    if (this.status !== 'connected') {
      return;
    }
    this.status = 'unknown';
    this.xeroService
      .disconnectXero()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        () => {
          this.status = 'disconnected';
        },
        (error) => {
          this.handleError(error);
        }
      );
  }

  openAccountTaxDialog() {
    this.dialog.open(AccountTaxDialogComponent);
  }

  importItems() {
    this.xeroService.importItems().subscribe(({ count }) => {
      this.toastService.success(`${count} items imported as Retail Products`);
    });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private checkStatus() {
    this.xeroService
      .getXeroStatus()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        (response: any) => {
          this.status = response.connected ? 'connected' : 'disconnected';
        },
        (error) => {
          this.handleError(error);
        }
      );
  }

  private handleError(error: any) {
    this.status = 'error';
    throw error;
  }

  importContacts() {
    this.importingContacts = true;
    this.xeroService.importContacts().subscribe({
      next: (response: any) =>
        this.toastService.success(`${response?.count ?? 0} contacts imported`),
      error: (error) =>
        this.toastService.error('An error occurred while importing contacts'),
      complete: () => (this.importingContacts = false)
    });
  }
}
